import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export type WindMethod = "wind" | "blast" | "staggered";

export interface WindParams {
  method: WindMethod;
  direction: "left" | "right";
  intensity: number;
}

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

export const Wind: Filter<WindParams> = {
  id: "wind",
  displayName: "Wind",
  defaultParams: { method: "wind", direction: "right", intensity: 50 },

  apply(input: PixelBuffer, params: WindParams, _selection: Selection | null): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;

    if (w === 0 || h === 0) { dst.set(src); return out; }

    const intensity = params.intensity / 100;

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const o = (y * w + x) * 4;
        const lum = src[o]! * 0.299 + src[o + 1]! * 0.587 + src[o + 2]! * 0.114;

        if (lum < 30) {
          dst[o] = src[o]!; dst[o + 1] = src[o + 1]!; dst[o + 2] = src[o + 2]!; dst[o + 3] = src[o + 3]!;
          continue;
        }

        const maxStreak = Math.round(intensity * 20);
        let streakR = 0, streakG = 0, streakB = 0, streakA = 0, streakCount = 0;

        for (let s = 0; s < maxStreak; s++) {
          const sx = params.direction === "right" ? x - s : x + s;
          if (sx < 0 || sx >= w) break;
          const si = (y * w + sx) * 4;
          const sLum = src[si]! * 0.299 + src[si + 1]! * 0.587 + src[si + 2]! * 0.114;
          if (sLum < 30) break;

          const fade = 1 - s / maxStreak;
          streakR += src[si]! * fade;
          streakG += src[si + 1]! * fade;
          streakB += src[si + 2]! * fade;
          streakA += src[si + 3]! * fade;
          streakCount += fade;

          if (params.method === "staggered" && Math.random() < 0.3) break;
        }

        if (streakCount > 0) {
          dst[o] = clamp8(Math.round(streakR / streakCount));
          dst[o + 1] = clamp8(Math.round(streakG / streakCount));
          dst[o + 2] = clamp8(Math.round(streakB / streakCount));
          dst[o + 3] = clamp8(Math.round(streakA / streakCount));
        } else {
          dst[o] = src[o]!; dst[o + 1] = src[o + 1]!; dst[o + 2] = src[o + 2]!; dst[o + 3] = src[o + 3]!;
        }
      }
    }
    return out;
  },
};
